import { ReactElement, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import {
  useFieldArray,
  Control,
  FieldErrors,
  FieldError,
} from "react-hook-form";
import cn from "classnames";
import { useTranslation, Trans } from "react-i18next";
import { FormField } from "oneclick-component/src/components/forms";
import { Button } from "oneclick-component/src/components/Button";
import {
  localizeDateTime,
  isoToDateTime,
} from "oneclick-component/src/utils/datetime";
import { Calendar } from "oneclick-component/src/components/inputs";
import DutyTimeSelect from "./DutyTimeSelect";
import { PlusCircleIcon } from "../../../icon";
import { MultiShiftFormValues } from "../form";
import {
  DEFAULT_SHIFT_START_TIME_HOUR,
  DEFAULT_SHIFT_START_TIME_MINUTE,
} from "../../../constants/shifts/create";

interface Props {
  control: Control<MultiShiftFormValues>;
  errors: FieldErrors<MultiShiftFormValues>;
  isEditing: boolean;
  now: DateTime;
  setTimeRangeStartMinuteByIndex: (index: number, value: number) => void;
  setTimeRangeEndMinuteByIndex: (index: number, value: number | null) => void;
}

const SingleDateSection = (props: Props): ReactElement => {
  const {
    errors,
    control,
    isEditing,
    now,
    setTimeRangeStartMinuteByIndex,
    setTimeRangeEndMinuteByIndex,
  } = props;
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "timeRanges",
  });

  const onAddTimeRange = useCallback(() => {
    append({
      startTime: {
        hour: DEFAULT_SHIFT_START_TIME_HOUR,
        minute: DEFAULT_SHIFT_START_TIME_MINUTE,
      },
      endTime: {
        hour: null,
        minute: null,
      },
    });
  }, [append]);

  const onRemoveTimeRange = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const dateError: FieldError | undefined = useMemo(() => {
    if (errors.recurringOptions != null && "date" in errors.recurringOptions) {
      return errors.recurringOptions.date as FieldError;
    }
  }, [errors.recurringOptions]);

  const makeSetTimeRangeStartMinute = useCallback(
    (index: number) => (value: number) => {
      setTimeRangeStartMinuteByIndex(index, value);
    },
    [setTimeRangeStartMinuteByIndex]
  );

  const makeSetTimeRangeEndMinute = useCallback(
    (index: number) => (value: number | null) => {
      setTimeRangeEndMinuteByIndex(index, value);
    },
    [setTimeRangeEndMinuteByIndex]
  );

  return (
    <>
      <div className={cn("flex", "gap-x-5")}>
        <FormField.Container
          className={"mb-5 flex-1"}
          label={t("shift.create.multiple.form.startDate")}
          required={true}
          errorMessage={dateError?.message}
        >
          <FormField.Control name={"recurringOptions.date"} control={control}>
            {({ field }) => (
              // FIXME: date display updated to have localization
              <Calendar
                {...field}
                disabled={isEditing}
                min={now.toFormat("yyyy-LL-dd")}
                hasError={dateError?.message != null}
                formattedValue={
                  field.value === ""
                    ? ""
                    : localizeDateTime(
                        isoToDateTime(field.value),
                        DateTime.DATE_MED
                      )
                }
              />
            )}
          </FormField.Control>
        </FormField.Container>
        <div className="flex-1" />
      </div>
      <div className={cn("mb-3", "flex", "flex-col", "gap-y-5")}>
        {fields.map((item, index) => (
          <DutyTimeSelect
            key={item.id}
            index={index}
            canRemove={fields.length > 1}
            onRemove={onRemoveTimeRange}
            errors={errors}
            control={control}
            isEditing={isEditing}
            setStartMinute={makeSetTimeRangeStartMinute(index)}
            setEndMinute={makeSetTimeRangeEndMinute(index)}
          />
        ))}
        <FormField.Container errorMessage={errors.timeRanges?.root?.message} />
      </div>
      <div>
        <Button
          prefixIcon={PlusCircleIcon}
          onClick={onAddTimeRange}
          theme="transparentPrimary"
          className={cn("pl-0", "pr-0")}
          iconClassName="!fill-none"
        >
          <Trans i18nKey="shift.create.multiple.addTimeRange" />
        </Button>
      </div>
    </>
  );
};

export default SingleDateSection;
